/**
 * Reducer para manejar el estado de los tickets Kanban
 * Implementa el principio de Responsabilidad Única (SRP)
 */
import {
  CREATE_TICKET_REQUEST,
  CREATE_TICKET_SUCCESS,
  CREATE_TICKET_FAILURE,
  GET_TICKETS_REQUEST,
  GET_TICKETS_SUCCESS,
  GET_TICKETS_FAILURE,
  APPEND_TICKETS_SUCCESS,
  // LOAD_MORE_TICKETS constants eliminadas - funcionalidad simplificada
  // UPDATE_TICKET_STATUS constants eliminadas - función innecesaria
  UPDATE_TICKET_REQUEST,
  UPDATE_TICKET_SUCCESS,
  UPDATE_TICKET_FAILURE,
  SET_DATE_FILTERS,
  CLEAR_DATE_FILTERS,
  SET_COLUMN_LOADING_MORE,
  // Paginación por páginas tradicional
  SET_COLUMN_PAGE,
  UPDATE_COLUMN_PAGINATION,
  LOAD_PAGE_TICKETS_SUCCESS,

} from './kanbanTypes';

/**
 * Estado inicial para el reducer de tickets
 */
const initialState = {
  items: [],
  loading: false,
  error: null,
  columnTickets: {},
  columnLoadingMore: {}, // Simple: {columnId: boolean} para loading states
  columnPagination: {}, // {columnId: {currentPage: 1, totalTickets: 0, hasMore: true}}
  filters: {
    startDate: null,
    endDate: null
  }
};

/**
 * Reducer para manejar el estado de los tickets Kanban
 */
const ticketReducer = (state = initialState, action) => {
  switch (action.type) {
    // Crear ticket
    case CREATE_TICKET_REQUEST:
      return {
        ...state,
        loading: true,
        error: null
      };
    case CREATE_TICKET_SUCCESS:
      const ticketColumnId = action.payload.column_id;

      // Verificar si el ticket ya existe en el estado global
      const ticketExistsInGlobal = state.items.some(
        ticket => String(ticket.id) === String(action.payload.id)
      );

      if (ticketExistsInGlobal) {
        return {
          ...state,
          items: state.items.map(ticket =>
            String(ticket.id) === String(action.payload.id) ? action.payload : ticket
          ),
          loading: false,
          error: null
        };
      }

      // Obtener tickets actuales para esta columna
      const currentColumnTickets =
        (state.columnTickets && state.columnTickets[ticketColumnId]) || [];

      // Actualizar el mapa de tickets por columna - nuevos tickets al inicio
      const newTicketsMap = {
        ...state.columnTickets,
        [ticketColumnId]: [action.payload, ...currentColumnTickets]
      };

      return {
        ...state,
        items: [action.payload, ...state.items],
        columnTickets: newTicketsMap,
        loading: false,
        error: null
      };
    case CREATE_TICKET_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload
      };

    // Obtener tickets
    case GET_TICKETS_REQUEST:
      return {
        ...state,
        loading: true,
        error: null
      };
    case GET_TICKETS_SUCCESS:
      const { columnId, tickets: newTickets } = action.payload;

      // Eliminar duplicados de los nuevos tickets
      const uniqueNewTickets = [];
      const ticketIds = new Set();

      newTickets.forEach(ticket => {
        const ticketId = String(ticket.id);
        if (!ticketIds.has(ticketId)) {
          ticketIds.add(ticketId);
          uniqueNewTickets.push(ticket);
        }
      });

      // Actualizar el objeto columnTickets para persistencia
      const updatedColumnTicketsMap = {
        ...state.columnTickets,
        [columnId]: uniqueNewTickets
      };

      // Filtrar los tickets existentes para evitar duplicados
      const existingTicketsFromOtherColumns = state.items.filter(ticket =>
        String(ticket.column_id) !== String(columnId)
      );

      // Combinar con los nuevos tickets
      const updatedTickets = [...existingTicketsFromOtherColumns, ...uniqueNewTickets];

      return {
        ...state,
        items: updatedTickets,
        columnTickets: updatedColumnTicketsMap,
        loading: false
      };
    case GET_TICKETS_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload.error
      };

    // Agregar tickets (para paginación)
    case APPEND_TICKETS_SUCCESS:
      const { columnId: appendColumnId, tickets: appendTickets } = action.payload;

      // Eliminar duplicados de los nuevos tickets
      const uniqueAppendTickets = [];
      const appendTicketIds = new Set();

      appendTickets.forEach(ticket => {
        const ticketId = String(ticket.id);
        if (!appendTicketIds.has(ticketId)) {
          appendTicketIds.add(ticketId);
          uniqueAppendTickets.push(ticket);
        }
      });

      // Actualizar el objeto columnTickets agregando los nuevos tickets
      const existingColumnTickets = state.columnTickets[appendColumnId] || [];
      
      // Filtrar tickets que ya existen en la columna (PREVENIR DUPLICADOS)
      const existingColumnTicketIds = new Set(existingColumnTickets.map(t => String(t.id)));
      const newTicketsOnly = uniqueAppendTickets.filter(t => !existingColumnTicketIds.has(String(t.id)));
      
      const updatedAppendColumnTicketsMap = {
        ...state.columnTickets,
        [appendColumnId]: [...existingColumnTickets, ...newTicketsOnly]
      };

      // Filtrar tickets existentes para evitar duplicados en items globales
      const existingGlobalTicketIds = new Set(state.items.map(t => String(t.id)));
      const newGlobalTickets = newTicketsOnly.filter(t => !existingGlobalTicketIds.has(String(t.id)));

      return {
        ...state,
        items: [...state.items, ...newGlobalTickets],
        columnTickets: updatedAppendColumnTicketsMap,
        loading: false
      };

    // Cargar más tickets - funcionalidad eliminada para simplificar



    // Actualizar ticket
    case UPDATE_TICKET_REQUEST:
      return {
        ...state,
        loading: true,
        error: null
      };
    case UPDATE_TICKET_SUCCESS:
      const { ticketId, updatedTicket } = action.payload || {};
      // Validación defensiva: si el payload no trae el shape esperado
      // ({ ticketId, updatedTicket }), no mutar el estado.
      if (!updatedTicket) {
        return state;
      }

      const currentColumnTicketsMap = { ...state.columnTickets };
      let oldColumnId = null;

      // Encontrar la columna de origen actual del ticket en el estado
      for (const colId in currentColumnTicketsMap) {
        if (currentColumnTicketsMap[colId].some(ticket => String(ticket.id) === String(ticketId))) {
          oldColumnId = colId;
          break;
        }
      }

      const newColumnId = updatedTicket?.column_id;
      const newColumnTicketsMap = { ...currentColumnTicketsMap };

      // Si el ticket se movió a otra columna (o se le asignó una por primera vez)
      if (newColumnId) {
          // Si el ticket estaba en una columna anterior, eliminarlo de ahí
          if (oldColumnId && oldColumnId !== newColumnId) {
               newColumnTicketsMap[oldColumnId] = newColumnTicketsMap[oldColumnId].filter(
                 ticket => String(ticket.id) !== String(ticketId)
               );
          }

          // Asegurarse de que la columna de destino exista en el mapa
          if (!newColumnTicketsMap[newColumnId]) {
              newColumnTicketsMap[newColumnId] = [];
          }

          // Verificar si el ticket ya existe en cualquier otra columna para evitar duplicación
          let ticketExistsInAnyColumn = false;
          let otherColumnWithTicket = null;
          
          // Buscar en todas las columnas excepto la columna de origen
          for (const colId in newColumnTicketsMap) {
            if (colId !== oldColumnId && newColumnTicketsMap[colId].some(ticket => 
              String(ticket.id) === String(ticketId))) {
              ticketExistsInAnyColumn = true;
              otherColumnWithTicket = colId;
              break;
            }
          }
          
          // Si el ticket ya existe en otra columna (que no es su origen ni destino), eliminarlo
          if (ticketExistsInAnyColumn && otherColumnWithTicket) {
                        
            // Verificar si el ticket duplicado tiene la marca _networkError, lo que indicaría
            // que fue creado durante un error de red y necesita ser manejado de forma especial
            const duplicatedTicket = newColumnTicketsMap[otherColumnWithTicket].find(
              ticket => String(ticket.id) === String(ticketId)
            );
            
            if (duplicatedTicket && duplicatedTicket._networkError) {
              // Si el duplicado es más reciente (creado con error de red), nos quedamos con él
              if (new Date(duplicatedTicket.created_at) > new Date(updatedTicket.created_at || 0)) {
                // Devolver el estado sin cambios
                return state;
              }
            }
            
            // Eliminar el ticket duplicado
            newColumnTicketsMap[otherColumnWithTicket] = newColumnTicketsMap[otherColumnWithTicket].filter(
              ticket => String(ticket.id) !== String(ticketId)
            );
          }

          // Evitar duplicados si por alguna razón el ticket ya está en la columna de destino
          newColumnTicketsMap[newColumnId] = newColumnTicketsMap[newColumnId].filter(
              ticket => String(ticket.id) !== String(ticketId)
          );

          // Agregar el ticket actualizado a la columna de destino (al principio para visibilidad)
          newColumnTicketsMap[newColumnId].unshift(updatedTicket);

      } else if (oldColumnId) {
          // Si el ticket no tiene newColumnId pero estaba en una columna, eliminarlo de ahí
           newColumnTicketsMap[oldColumnId] = newColumnTicketsMap[oldColumnId].filter(
             ticket => String(ticket.id) !== String(ticketId)
           );
      }

      // Actualizar la lista general de items también
       const updatedItems = state.items.map(item => 
           String(item.id) === String(ticketId) ? updatedTicket : item
       );
        // Asegurarnos de que el ticket actualizado esté en la lista general si no lo estaba
       if (updatedTicket && !updatedItems.some(item => String(item.id) === String(ticketId))) {
           updatedItems.push(updatedTicket);
       }

      return {
        ...state,
        items: updatedItems, // Mantener items actualizados
        columnTickets: newColumnTicketsMap, // Fuente principal para las columnas
        loading: false,
        error: null
      };
    case UPDATE_TICKET_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload.error
      };



    // Establecer filtros de fecha
    case SET_DATE_FILTERS:
      return {
        ...state,
        columnTickets: {}, // Limpiar el cache de tickets
        loading: true, // Mantener el loading activo durante el filtrado
        filters: {
          startDate: action.payload.startDate || null,
          endDate: action.payload.endDate || null,
          assignedAgent: action.payload.assignedAgent || null
        }
      };

    // Limpiar filtros de fecha
    case CLEAR_DATE_FILTERS:
      return {
        ...state,
        columnTickets: {},
        loading: true, // Mantener el loading activo durante la limpieza
        filters: {
          startDate: null,
          endDate: null,
          assignedAgent: null
        }
      };



    // Estado simple para loading de más tickets
    case SET_COLUMN_LOADING_MORE:
      return {
        ...state,
        columnLoadingMore: {
          ...state.columnLoadingMore,
          [action.payload.columnId]: action.payload.loading
        }
      };

    // Paginación por páginas tradicional
    case SET_COLUMN_PAGE:
      return {
        ...state,
        columnPagination: {
          ...state.columnPagination,
          [action.payload.columnId]: {
            ...state.columnPagination[action.payload.columnId],
            currentPage: action.payload.page
          }
        }
      };

    case UPDATE_COLUMN_PAGINATION:
      return {
        ...state,
        columnPagination: {
          ...state.columnPagination,
          [action.payload.columnId]: {
            ...state.columnPagination[action.payload.columnId],
            ...action.payload.pagination
          }
        }
      };

    case LOAD_PAGE_TICKETS_SUCCESS:
      const { columnId: pageColumnId, tickets: pageTickets, page, totalEstimated } = action.payload;

      // Para paginación por páginas, REEMPLAZAMOS los tickets en lugar de agregar
      const pageTicketsMap = {
        ...state.columnTickets,
        [pageColumnId]: pageTickets
      };

      // Actualizar también la información de paginación
      const updatedPaginationMap = {
        ...state.columnPagination,
        [pageColumnId]: {
          currentPage: page,
          totalTickets: totalEstimated || pageTickets.length,
          hasMore: pageTickets.length === 5
        }
      };

      // Filtrar tickets existentes para evitar duplicados en items globales
      const existingItemsFiltered = state.items.filter(ticket =>
        String(ticket.column_id) !== String(pageColumnId)
      );

      return {
        ...state,
        items: [...existingItemsFiltered, ...pageTickets],
        columnTickets: pageTicketsMap,
        columnPagination: updatedPaginationMap,
        loading: false
      };



    default:
      return state;
  }
};

export default ticketReducer;
